import { useState } from "react";
import { Plus, Pencil, Trash2, CornerDownRight, ExternalLink } from "lucide-react";
import { useList } from "./AdminApp";
import { PageTitle } from "./Layout";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Field, I18nInput } from "../../components/admin/Fields";
import SortableList from "../../components/admin/SortableList";

const TYPES = [
  ["builtin", "Hazır bölüm"], ["page", "Sayfa"], ["news", "Yazı"], ["news_category", "Yazı kategorisi"], ["album", "Video albümü"],
  ["video_category", "Video kategorisi"], ["internal", "İç link (elle)"], ["external", "Dış link (elle)"], ["none", "Sadece başlık (alt menü grubu)"],
];
const BUILTIN = [["home", "Anasayfa"], ["news", "Yazılar"], ["videos", "Video Galerisi"], ["gallery", "Resim Galerisi"], ["contact", "İletişim"]];
const SOURCES = { page: "pages", news: "news", news_category: "news_categories", album: "video_albums", video_category: "video_categories" };
const sel = "w-full border rounded-md h-9 px-3 text-sm bg-white";

function TargetSelect({ type, value, onChange }) {
  const { data = [] } = useList(SOURCES[type] || "pages");
  return (
    <select className={sel} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="menu-target-select">
      <option value="">— Seçin —</option>
      {data.map((d) => <option key={d.id} value={d.id}>{tr(d.title || d.name)}</option>)}
    </select>
  );
}

function ItemDialog({ item, roots, onClose }) {
  const [f, setF] = useState(item);
  const { save } = useCrud("menu_items");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (await save({ ...f, parent_id: f.parent_id || null })) onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg font-body" data-testid="menu-item-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Menü öğesini düzenle" : "Yeni menü öğesi"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <I18nInput label="Görünen ad" value={f.label} onChange={set("label")} testid="menu-label-input" maxLength={60} />
          <Field label="Üst menü" hint="Bir üst öğe seçerseniz bu öğe açılır alt menüde görünür.">
            <select className={sel} value={f.parent_id || ""} onChange={(e) => set("parent_id")(e.target.value || null)} data-testid="menu-parent-select">
              <option value="">— Ana menü —</option>
              {roots.filter((r) => r.id !== f.id).map((r) => <option key={r.id} value={r.id}>{tr(r.label)}</option>)}
            </select>
          </Field>
          <Field label="Bağlantı türü">
            <select className={sel} value={f.link_type} onChange={(e) => setF({ ...f, link_type: e.target.value, target_id: null, url: "" })} data-testid="menu-type-select">
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          {f.link_type === "builtin" && (
            <Field label="Bölüm">
              <select className={sel} value={f.url || "home"} onChange={(e) => set("url")(e.target.value)} data-testid="menu-builtin-select">
                {BUILTIN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          )}
          {SOURCES[f.link_type] && <Field label="Hedef"><TargetSelect type={f.link_type} value={f.target_id} onChange={set("target_id")} /></Field>}
          {["internal", "external"].includes(f.link_type) && (
            <Field label="Adres" hint={f.link_type === "internal" ? "Örn: /vizyonum" : "Örn: https://ornek.com"}>
              <Input value={f.url || ""} onChange={(e) => set("url")(e.target.value)} data-testid="menu-url-input" />
            </Field>
          )}
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.new_tab} onCheckedChange={set("new_tab")} data-testid="menu-newtab-switch" /> Yeni sekmede aç</label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Vazgeç</Button>
            <Button data-testid="menu-item-save">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ItemRow({ it, onEdit, onDelete, child }) {
  const type = TYPES.find((t) => t[0] === it.link_type)?.[1];
  return (
    <div className="flex items-center gap-3" data-testid={`menu-row-${it.id}`}>
      {child && <CornerDownRight size={14} className="text-stone-300" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{tr(it.label)} {it.new_tab && <ExternalLink size={11} className="inline text-stone-400" />}</p>
        <p className="text-[11px] text-stone-400">{type}{it.url ? ` · ${it.url}` : ""}</p>
      </div>
      <Button size="icon" variant="ghost" onClick={() => onEdit(it)} data-testid={`menu-edit-${it.id}`}><Pencil size={14} /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(it)} data-testid={`menu-delete-${it.id}`}><Trash2 size={14} className="text-red-500" /></Button>
    </div>
  );
}

export default function MenuAdmin() {
  const { data = [] } = useList("menu_items");
  const { remove, reorder } = useCrud("menu_items");
  const [editing, setEditing] = useState(null);
  const roots = data.filter((m) => !m.parent_id);
  const del = (it) => remove(it.id, `"${tr(it.label)}"${data.some((c) => c.parent_id === it.id) ? " ve alt öğeleri" : ""}`);
  const blank = { label: { tr: "" }, link_type: "page", parent_id: null, new_tab: false };
  return (
    <>
      <PageTitle title="Menü yönetimi" sub="Sürükleyip bırakarak sıralayın. Alt menü için öğeyi bir üst öğeye bağlayın."
        action={<Button onClick={() => setEditing(blank)} data-testid="new-menu-item-button"><Plus size={15} className="mr-1" />Yeni öğe</Button>} />
      <SortableList items={roots} onReorder={reorder} className="space-y-2" render={(r) => (
        <div>
          <ItemRow it={r} onEdit={setEditing} onDelete={del} />
          {data.some((c) => c.parent_id === r.id) && (
            <div className="mt-2 ml-6">
              <SortableList items={data.filter((c) => c.parent_id === r.id)} onReorder={reorder} render={(c) => <ItemRow it={c} child onEdit={setEditing} onDelete={del} />} />
            </div>
          )}
        </div>
      )} />
      {editing && <ItemDialog item={editing} roots={roots} onClose={() => setEditing(null)} />}
    </>
  );
}
